import { z } from 'zod';
import {
  alertStateEnum,
  isoDateTime,
  paginated,
  severityEnum,
  uuid,
  zoneCode,
} from '@/api/schemas/common';

/** Alerts — shared-contract.md §4 and §6. */

export const ALERT_CATEGORIES = [
  'high_occupancy',
  'area_full',
  'traffic_congestion',
  'long_stay',
  'obstruction',
  'sensor_fault',
  'gateway_issue',
  'unauthorized_usage',
] as const;

export type AlertCategory = (typeof ALERT_CATEGORIES)[number];

/**
 * Two internal categories are **not shown to operations**: `signage_issue` and
 * `system_health`. They exist on the wire; the filter rail lists the eight.
 */
export const INTERNAL_ALERT_CATEGORIES = ['signage_issue', 'system_health'] as const;

export const alertCategoryEnum = z.enum(ALERT_CATEGORIES);

export const ALERT_CATEGORY_LABELS: Readonly<Record<AlertCategory, string>> = {
  high_occupancy: 'High occupancy',
  area_full: 'Area full',
  traffic_congestion: 'Traffic congestion',
  long_stay: 'Long stay',
  obstruction: 'Obstruction',
  sensor_fault: 'Sensor fault',
  gateway_issue: 'Gateway issue',
  unauthorized_usage: 'Unauthorised usage',
};

const trendPoint = z.object({ at: isoDateTime, value: z.number() });

/**
 * `context.kind` discriminates the category-specific context block — the thing
 * that turns an alert into a decision (W-3.4). One variant per category.
 */
export const alertContextSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('sensor_fault'),
    batteryPct: z.number().int(),
    bestRssi: z.number(),
    gatewayCount: z.number().int().nonnegative(),
    neighbourNote: z.string(),
  }),
  z.object({
    kind: z.literal('obstruction'),
    stdDevMm: z.number(),
    neighbourTurnover: z.number(),
    hoursObstructed: z.number(),
  }),
  z.object({
    kind: z.literal('high_occupancy'),
    zoneOccupancyPct: z.number(),
    thresholdPct: z.number(),
    trend: z.array(trendPoint),
  }),
  z.object({
    kind: z.literal('area_full'),
    freeBays: z.number().int().nonnegative(),
    threshold: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('traffic_congestion'),
    pointsRise: z.number(),
    windowMinutes: z.number().int(),
    trend: z.array(trendPoint),
  }),
  z.object({
    kind: z.literal('long_stay'),
    hours: z.number(),
    plateMasked: z.string(),
    parkedAt: isoDateTime,
  }),
  z.object({
    kind: z.literal('gateway_issue'),
    gatewayEui: z.string(),
    silentMinutes: z.number().int(),
    baysAffected: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('unauthorized_usage'),
    mode: z.string(),
    graceSeconds: z.number().int(),
    durationSeconds: z.number().int(),
  }),
]);

export const alertScopeSchema = z.object({
  type: z.enum(['facility', 'zone', 'bay', 'device', 'gateway']),
  code: z.string(),
  zoneCode: zoneCode.nullable(),
  zoneName: z.string().nullable(),
});

export const alertSchema = z.object({
  id: z.string(),
  category: alertCategoryEnum,
  severity: severityEnum,
  state: alertStateEnum,
  /** Acknowledging leaves the state `open`. Acknowledge ≠ resolve (§9.8). */
  acknowledged: z.boolean(),
  acknowledgedBy: z.string().nullable().optional(),
  acknowledgedAt: isoDateTime.nullable().optional(),
  raisedAt: isoDateTime,
  resolvedAt: isoDateTime.nullable().optional(),
  scope: alertScopeSchema,
  rule: z.string(),
  trigger: z.string(),
  occurrences: z.number().int().positive(),
  grouped: z.boolean(),
  notifiedVia: z.array(z.string()),
  notificationGroup: z.string().nullable(),
  escalatesAt: isoDateTime.nullable(),
  /** Labelled `auto` in the queue so it is never confused with a human decision. */
  autoResolved: z.boolean(),
  context: alertContextSchema,
});

export const alertEventSchema = z.object({
  at: isoDateTime,
  type: z.enum(['raised', 'acknowledged', 'escalated', 'resolved']),
  actor: z.string().nullable(),
  note: z.string().nullable(),
});

export const alertDetailSchema = alertSchema.extend({ events: z.array(alertEventSchema) });

export const alertListSchema = paginated(alertSchema).extend({
  /** 7-day counts per category, from the same query, so a zero still appears. */
  categoryCounts: z.record(alertCategoryEnum, z.number().int().nonnegative()),
});

export const acknowledgeRequestSchema = z.object({
  note: z.string().optional(),
  /** The mobile app queues acknowledgements through the outbox like any write. */
  clientRequestId: z.string().optional(),
});

export const bulkAcknowledgeRequestSchema = z.object({
  alertIds: z.array(z.string()).min(1),
  note: z.string().optional(),
  clientRequestId: z.string().optional(),
});

export const resolveRequestSchema = z.object({
  /** Required when the severity was major or critical; a missing note is 422. */
  note: z.string().optional(),
  clientRequestId: z.string().optional(),
});

/* ── Alert rules ─────────────────────────────────────────────────────────── */

export const quietHoursSchema = z.object({
  enabled: z.boolean(),
  from: z.string(),
  to: z.string(),
  /** Critical alerts are exempt from quiet hours. */
  criticalExempt: z.boolean(),
});

export const alertRuleSchema = z.object({
  id: uuid,
  code: alertCategoryEnum,
  name: z.string(),
  scopes: z.array(z.enum(['facility', 'zone', 'bay', 'device', 'gateway'])),
  enabled: z.boolean(),
  severity: severityEnum,
  raiseCondition: z.string(),
  resolveCondition: z.string(),
  /** JSON Schema. The editor renders this and nothing else. */
  parameters: z.record(z.unknown()),
  parameterValues: z.record(z.unknown()),
  cooldownS: z.number().int().nonnegative(),
  escalateAfterS: z.number().int().nonnegative(),
  notificationGroupId: uuid.nullable(),
  /** Always on and not togglable. */
  inApp: z.literal(true),
  email: z.boolean(),
  push: z.boolean(),
  /** A fixed, disabled value: quiet hours are not configurable per rule, so the editor does not show them. */
  quietHours: quietHoursSchema,
  /** Sent back as `expectedVersion` on every save; a stale one is refused 412. */
  version: z.number().int().nonnegative(),
  /** Rendered as an amber proposal notice — our interpretation, not a reading. */
  isProposal: z.boolean().optional(),
  proposalNote: z.string().optional(),
});

export const alertRuleListSchema = z.object({ items: z.array(alertRuleSchema) });

/** `POST /alert-rules/{id}/test` — reports what it would do and raises nothing. */
export const alertRuleTestResultSchema = z.object({
  wouldRaise: z.number().int().nonnegative(),
  bays: z.number().int().nonnegative(),
  window: z.string(),
  samples: z.array(z.object({ at: isoDateTime, scopeCode: z.string(), detail: z.string() })),
});

export const notificationGroupSchema = z.object({
  id: uuid,
  name: z.string(),
  recipients: z.array(
    z
      .object({
        /** Exactly one of the three per row; two is refused 422. */
        userId: uuid.nullable(),
        roleCode: z.string().nullable(),
        email: z.string().nullable(),
      })
      .refine((row) => [row.userId, row.roleCode, row.email].filter(Boolean).length === 1, {
        message: 'A recipient is exactly one of user, role or e-mail',
      }),
  ),
});

/** `GET /alerts/{id}/deliveries` — what was sent for one alert, and what became of it. */
export const alertDeliverySchema = z.object({
  event: z.string(),
  channel: z.enum(['email', 'push']).or(z.string()),
  recipient: z.string(),
  status: z.string(),
  attempts: z.number().int().nonnegative(),
  error: z.string().nullable(),
  createdAt: isoDateTime,
  sentAt: isoDateTime.nullable(),
});

export const alertDeliveryListSchema = z.object({ items: z.array(alertDeliverySchema) });

/** `POST`/`PUT /notification-groups` body. */
export const notificationGroupRequestSchema = z.object({
  name: z.string().min(1),
  recipients: notificationGroupSchema.shape.recipients,
});

export type Alert = z.infer<typeof alertSchema>;
export type AlertDelivery = z.infer<typeof alertDeliverySchema>;
export type AlertDetail = z.infer<typeof alertDetailSchema>;
export type AlertContext = z.infer<typeof alertContextSchema>;
export type AlertRule = z.infer<typeof alertRuleSchema>;
export type AlertRuleTestResult = z.infer<typeof alertRuleTestResultSchema>;
export type NotificationGroup = z.infer<typeof notificationGroupSchema>;
